import { MultipleFileViewCell, SelectCell } from '@/components/Table/components/Cell'
import DatePickerCell from '@/components/Table/components/Cell/DatePickerCell'
import { ApprovalStatus } from '@/features/offers/constants'
import { setApprovalInvitedUsers } from '@/stores/offers/offersSlice'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { Button, Flex, Input, Text } from '@chakra-ui/react'
import moment from 'moment'

export const APPROVAL_TABLE_TYPE = {
  QUOTATION_APPROVAL: 'quotationApproval',
  BOOKING_APPROVAL: 'bookingApproval',
  PR_APPROVAL: 'prApproval',
  GCPR_APPROVAL: 'gcprApproval',
}

export const getStatusCellColor = (status) => {
  let color = 'black'
  let backgroundColor = 'white'
  switch (status) {
    case ApprovalStatus.REQUIRED:
      color = 'black'
      backgroundColor = 'yellow.200'
      break
    case ApprovalStatus.APPROVED:
      backgroundColor = 'green.500'
      color = 'white'

      break
    case ApprovalStatus.REJECTED:
      backgroundColor = 'red.500'
      color = 'white'
      break
  }

  return {
    color,
    backgroundColor,
  }
}

export const buildApproverTableColumn = ({ columnHelper, dispatch, updateInvitationParameter, tableType }) => {
  return [
    columnHelper.accessor('type', {
      size: 130,
      cell: (cell) => {
        return (
          <SelectCell
            {...cell}
            extraDisable={cell?.row?.original?.disableEditType ?? true}
            options={cell?.row?.original?.typeOptions}
          />
        )
      },
    }),
    columnHelper.accessor('user', {
      header: () => <>Name</>,
      size: 150,
      cell: (cell) => {
        const original = cell?.row?.original
        const disableEditUser = cell?.row?.original?.disableEditUser ?? true

        //For Approval - others
        let finalUserOptions = original?.userOptions || []

        if (original?.type === 'other') {
          const selectedUserIds = cell?.table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
          finalUserOptions = finalUserOptions.filter(
            (option) => !selectedUserIds.includes(option.value) || option.value === getValue()
          )
        }
        return <SelectCell {...cell} options={finalUserOptions} extraDisable={disableEditUser} />
      },
    }),
    columnHelper.accessor('dueDate', {
      size: 150,
      cell: (cell) => {
        const disableEditDueDate = cell?.row?.original?.disableEditDueDate ?? true
        return <DatePickerCell {...cell} extraDisable={disableEditDueDate} maxDate={cell?.row?.original?.maxDueDate} />
      },
    }),
    columnHelper.accessor('status', {
      header: () => <>Approval Status</>,
      cell: ({ row: { original }, getValue }) => {
        const { color, backgroundColor } = getStatusCellColor(getValue())

        let displayStatus = getValue()
        if (displayStatus === ApprovalStatus.APPROVED && original?.approvedDate) {
          displayStatus = `${displayStatus} (${moment(original?.approvedDate).format('DD-MMM-YYYY')})`
        }

        return (
          <Text color={color} bgColor={backgroundColor} py="2">
            {displayStatus}
          </Text>
        )
      },
    }),
    columnHelper.accessor('remark', {
      size: 350,
      cell: ({ row: { index, original }, column: { id: columnId }, getValue, table }) => {
        const value = getValue()

        const disableEditRemark = table.options.meta?.disabled || (original?.disableEditRemark ?? true)

        return (
          <Flex direction="column" alignItems={'start'} gap="2">
            <Flex w="100%">
              {!disableEditRemark && (
                <Input
                  type="text"
                  value={value || ''}
                  onChange={({ target: { value: newValue } }) => {
                    table.options.meta?.updateData(index, columnId, newValue)
                  }}
                ></Input>
              )}

              {disableEditRemark && <Text>{value}</Text>}
            </Flex>

            {original.delegationRemark !== '' && (
              <Text as={'i'} color={original.isEffective ? 'black.400' : 'teal.400'}>
                {original.delegationRemark}
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('attachments', {
      cell: (cell) => <MultipleFileViewCell {...cell} title={'Approver Attachments'} />,
    }),

    columnHelper.display({
      id: 'action',
      header: () => <Text>Action</Text>,
      size: 100,
      cell: ({ row }) => {
        const approverIndex = row.index
        const disableRemoveApprover = row?.original?.disableRemoveApprover
        return (
          <Flex direction="row" gap="2">
            <Button
              size="md"
              colorScheme="teal"
              onClick={() => {
                const invitedUsers = row?.original?.invitedUsers || []
                dispatch(setApprovalInvitedUsers(invitedUsers))
                dispatch(
                  showModal({
                    modalType: MODAL_TYPES.INVITATION_DETAIL,
                    modalProps: {
                      updateInvitationParameter: {
                        ...updateInvitationParameter,
                        approverIndex,
                      },
                      approvalRole: row?.original.type,
                      canManageInvitations: row?.original?.canManageInvitations,
                      tableType: tableType,
                    },
                  })
                )
              }}
            >
              {row?.original?.invitationButtonText ?? 'Invitation'}
            </Button>
            {!disableRemoveApprover && (
              <Button
                colorScheme="red"
                onClick={() => {
                  removeApprover(index)
                }}
              >
                Delete
              </Button>
            )}
          </Flex>
        )
      },
    }),
  ]
}

export const getInvitationButtonText = (inviteeDetail) => {
  if (!Array.isArray(inviteeDetail)) {
    return 'Invitation'
  }

  const totalInvitedCount = inviteeDetail.length
  const repliedInvitedCount = inviteeDetail.filter((invitee) => invitee?.status === 'Replied').length

  return repliedInvitedCount === 0 && totalInvitedCount === 0
    ? 'Invitation'
    : `Invitation (${repliedInvitedCount}/${totalInvitedCount})`
}
